import React, { useState, useEffect } from "react";

import { useLocation } from "react-router-dom";

import {
  Typography,
  Grid,
  CircularProgress,
  Container,
  FormControlLabel,
  Checkbox,
} from "@mui/material";

import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Brush,
} from "recharts";

import { apiGet } from "@services/API";

export default function ObservationTabletChart({ ...props }) {
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const patientId = searchParams.get("id");
  const token = searchParams.get("token");

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  const [isRespiratoryRateSelected, setIsRespiratoryRateSelected] =
    useState(true);

  const [isSpo2Selected, setIsSpo2Selected] = useState(true);

  const [isHeartRateSelected, setIsHeartRateSelected] = useState(true);
  const [isSystolicSelected, setIsSystolicSelected] = useState(true);
  const [isDiastolicSelected, setIsDiastolicSelected] = useState(true);

  const [isTemperatureSelected, setIsTemperatureSelected] = useState(true);

  const [isWeightSelected, setIsWeightSelected] = useState(true);
  const [isPainSelected, setIsPainSelected] = useState(true);

  const [isNEWSSelected, setIsNEWSSelected] = useState(true);
  const [isBloodSugarSelected, setIsBloodSugarSelected] = useState(true);

  const [isAllSelected, setIsAllSelected] = useState(true);

  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(0);

  const isValidPatientId = patientId && !isNaN(patientId);
  const isValidToken =
    token && typeof token === "string" && token.trim() !== "";

  useEffect(() => {
    if (isValidPatientId && isValidToken) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const allSelected =
      isRespiratoryRateSelected &&
      isSpo2Selected &&
      isHeartRateSelected &&
      isSystolicSelected &&
      isDiastolicSelected &&
      isTemperatureSelected &&
      isWeightSelected &&
      isPainSelected &&
      isNEWSSelected &&
      isBloodSugarSelected;

    if (!allSelected) {
      setIsAllSelected(false);
    }
  }, [
    isRespiratoryRateSelected,
    isSpo2Selected,
    isHeartRateSelected,
    isSystolicSelected,
    isDiastolicSelected,
    isTemperatureSelected,
    setIsWeightSelected,
    isPainSelected,
    isNEWSSelected,
    isBloodSugarSelected,
  ]);

  const fetchData = async () => {
    try {
      const [observationsResponse] = await Promise.all([
        apiGet(
          `/observations/${patientId}/0`,
          {
            headers: { "x-access-token": token },
          },
          history
        ),
      ]);

      if (observationsResponse.data.length > 0) {
        const sortedData = observationsResponse.data.sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
        );
        // const transformedData = sortedData.map((item) => {
        //   return {
        //     ...item,
        //     sys: item.sys === 0 ? null : item.sys,
        //     dia: item.dia === 0 ? null : item.dia,
        //     bpm: item.bpm === 0 ? null : item.bpm,
        //     temperature: item.temperature === 0 ? null : item.temperature,
        //     weight: item.weight === 0 ? null : item.weight,
        //     pain_level: item.pain_level === 0 ? null : item.pain_level,
        //     barthel_score: item.barthel_score === 0 ? null : item.barthel_score,
        //     frailty: item.frailty === 0 ? null : item.frailty,
        //     oximeter: item.oximeter === 0 ? null : item.oximeter,
        //     resp_rate: item.resp_rate === 0 ? null : item.resp_rate,
        //   };
        // });

        setEndIndex(sortedData.length - 1);
        setData(sortedData);
      } else {
        setData(null);
      }

      setLoading(false);
    } catch (error) {
      setData(null);
      setLoading(false);
    }
  };

  const handleZoom = (startIndex, endIndex) => {
    setStartIndex(startIndex);
    setEndIndex(endIndex);
  };

  const formatDate = (date) => {
    const options = { day: "2-digit", month: "2-digit", year: "numeric" };
    return new Intl.DateTimeFormat("en-GB", options).format(new Date(date));
  };

  const formatLabelDate = (value) =>
    new Date(value).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const setAllSelected = (value) => {
    if (value) {
      setIsRespiratoryRateSelected(true);
      setIsSpo2Selected(true);
      setIsHeartRateSelected(true);
      setIsSystolicSelected(true);
      setIsDiastolicSelected(true);
      setIsTemperatureSelected(true);
      setIsWeightSelected(true);
      setIsPainSelected(true);
      setIsNEWSSelected(true);
      setIsBloodSugarSelected(true);
      setIsAllSelected(true);
    } else {
      setIsRespiratoryRateSelected(false);
      setIsSpo2Selected(false);
      setIsHeartRateSelected(false);
      setIsSystolicSelected(false);
      setIsDiastolicSelected(false);
      setIsTemperatureSelected(false);
      setIsWeightSelected(false);
      setIsPainSelected(false);
      setIsNEWSSelected(false);
      setIsBloodSugarSelected(false);
      setIsAllSelected(false);
    }
  };

  return (
    <Container style={{ backgroundColor: "#F2F2F2", padding: 5 }}>
      <Grid
        container
        spacing={1}
        style={{
          display: "flex",
          alignItems: "center",
        }}
      >
        {loading ? (
          <Grid item xs={12} md={12} style={{}}>
            <CircularProgress color="primary" />
          </Grid>
        ) : data !== null ? (
          <>
            <Grid
              item
              xs={12}
              md={12}
              style={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isRespiratoryRateSelected}
                    onChange={(e) =>
                      setIsRespiratoryRateSelected(e.target.checked)
                    }
                    name="resp_rate"
                    color="primary"
                  />
                }
                label={
                  <span style={{ fontSize: "15px" }}>Respiratory Rate</span>
                }
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSpo2Selected}
                    onChange={(e) => setIsSpo2Selected(e.target.checked)}
                    name="oximeter"
                    color="primary"
                  />
                }
                label={<span style={{ fontSize: "15px" }}>Spo2</span>}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isHeartRateSelected}
                    onChange={(e) => setIsHeartRateSelected(e.target.checked)}
                    name="bpm"
                    color="primary"
                  />
                }
                label={<span style={{ fontSize: "15px" }}>Heart Rate</span>}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSystolicSelected}
                    onChange={(e) => setIsSystolicSelected(e.target.checked)}
                    name="sys"
                    color="primary"
                  />
                }
                label={<span style={{ fontSize: "15px" }}>Systolic</span>}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isDiastolicSelected}
                    onChange={(e) => setIsDiastolicSelected(e.target.checked)}
                    name="dia"
                    color="primary"
                  />
                }
                label={<span style={{ fontSize: "15px" }}>Diastolic</span>}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isTemperatureSelected}
                    onChange={(e) => setIsTemperatureSelected(e.target.checked)}
                    name="temperature"
                    color="primary"
                  />
                }
                label={<span style={{ fontSize: "15px" }}>Temperature</span>}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isWeightSelected}
                    onChange={(e) => setIsWeightSelected(e.target.checked)}
                    name="weight"
                    color="primary"
                  />
                }
                label={<span style={{ fontSize: "15px" }}>Weight</span>}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isPainSelected}
                    onChange={(e) => setIsPainSelected(e.target.checked)}
                    name="pain_level"
                    color="primary"
                  />
                }
                label={<span style={{ fontSize: "15px" }}>Pain</span>}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isNEWSSelected}
                    onChange={(e) => setIsNEWSSelected(e.target.checked)}
                    name="news_score"
                    color="primary"
                  />
                }
                label={<span style={{ fontSize: "15px" }}>NEWS</span>}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isBloodSugarSelected}
                    onChange={(e) => setIsBloodSugarSelected(e.target.checked)}
                    name="blood_sugar"
                    color="primary"
                  />
                }
                label={<span style={{ fontSize: "15px" }}>Blood Sugar</span>}
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={isAllSelected}
                    onChange={(e) => setAllSelected(e.target.checked)}
                    name="all_selected"
                    color="primary"
                  />
                }
                label={<span style={{ fontSize: "15px" }}>All</span>}
              />
            </Grid>
          </>
        ) : (
          <Grid item xs={12}>
            <Typography variant="body1">No records to display</Typography>
          </Grid>
        )}
      </Grid>

      {data !== null && (
        <ResponsiveContainer width="100%" height={545}>
          <LineChart
            data={data}
            margin={{ top: 0, left: 65, right: 125, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis dataKey="timestamp" tickFormatter={formatDate} />
            <YAxis />
            <Tooltip labelFormatter={formatLabelDate} />
            <Legend
              verticalAlign="top"
              layout="horizontal"
              wrapperStyle={{ padding: 10 }}
            />

            {isRespiratoryRateSelected && (
              <Line
                type="monotone"
                connectNulls
                dataKey="resp_rate"
                stroke="#be308f"
                name="Respiratory Rate"
              />
            )}

            {isSpo2Selected && (
              <Line
                type="monotone"
                connectNulls
                dataKey="oximeter"
                stroke="#00BFFF"
                name="SpO2"
              />
            )}

            {isHeartRateSelected && (
              <Line
                type="monotone"
                connectNulls
                dataKey="bpm"
                stroke="#FF5733"
                name="Heart Rate"
              />
            )}

            {isSystolicSelected && (
              <Line
                type="monotone"
                connectNulls
                dataKey="sys"
                stroke="#FFA500"
                name="Systolic"
              />
            )}

            {isDiastolicSelected && (
              <Line
                type="monotone"
                connectNulls
                dataKey="dia"
                stroke="#9370DB"
                name="Diastolic"
              />
            )}

            {isTemperatureSelected && (
              <Line
                type="monotone"
                connectNulls
                dataKey="temperature"
                stroke="#2ade2a"
                name="Temperature"
              />
            )}

            {isWeightSelected && (
              <Line
                type="monotone"
                connectNulls
                dataKey="weight"
                stroke="#5e0c66"
                name="Weight"
              />
            )}

            {isPainSelected && (
              <Line
                type="monotone"
                connectNulls
                dataKey="pain_level"
                stroke="#3DAC95"
                name="Pain"
              />
            )}

            {isNEWSSelected && (
              <Line
                type="monotone"
                connectNulls
                dataKey="news_score"
                stroke="#5C6BC0"
                name="NEWS"
              />
            )}

            {isBloodSugarSelected && (
              <Line
                type="monotone"
                connectNulls
                dataKey="blood_sugar"
                stroke="#a88888"
                name="Blood Sugar"
              />
            )}

            <Brush
              stroke="#000"
              fill="#C2D1C0"
              dataKey="timestamp"
              height={35}
              //width={200}
              startIndex={startIndex}
              endIndex={endIndex}
              tickFormatter={formatLabelDate}
              travellerWidth={25}
              alwaysShowText={true}
              onChange={({ startIndex, endIndex }) =>
                handleZoom(startIndex, endIndex)
              }
            />
          </LineChart>
        </ResponsiveContainer>
      )}
    </Container>
  );
}
